import { useState } from "react";
import type { InvoiceSummary } from "@/api/invoices";
import { Checkbox } from "@/components/ui/checkbox";
import { TextField } from "@/components/ui/field";
import { formatDate, formatMoney } from "@/lib/format";
import { centsToAmount, formatTotals, totalsByCurrency } from "@/lib/money";

/** What one invoice takes of the money, and any shortfall waived on it. */
export type InvoiceLine = { amount: string; write_off: string };

export function cents(value: string): number {
  return Math.round(Number(value || "0") * 100);
}

function groupOf(invoice: InvoiceSummary): string {
  const vessel = invoice.vessel_name ?? "No vessel";
  return invoice.voyage ? `${vessel} · Voyage ${invoice.voyage}` : vessel;
}

/** Vessel, then voyage, then invoice number: the order the receipt lists them in. */
export function inReceiptOrder(invoices: InvoiceSummary[]): InvoiceSummary[] {
  return [...invoices].sort(
    (a, b) =>
      groupOf(a).localeCompare(groupOf(b)) || (a.invoice_no ?? "").localeCompare(b.invoice_no ?? ""),
  );
}

/**
 * The client's open invoices by vessel and voyage, to tick the ones this money settles: one
 * call, a whole voyage, or several voyages. Each ticked invoice takes its balance unless told
 * otherwise. One receipt is in one currency, so the first invoice ticked decides it.
 */
export function InvoicePicker({
  id,
  invoices,
  lines,
  onChange,
}: {
  id: string;
  invoices: InvoiceSummary[];
  lines: Record<string, InvoiceLine>;
  onChange: (lines: Record<string, InvoiceLine>) => void;
}) {
  const [find, setFind] = useState("");
  const currency = invoices.find((invoice) => invoice.id in lines)?.currency;
  const needle = find.trim().toLowerCase();
  const shown = invoices.filter(
    (invoice) =>
      !needle ||
      [invoice.invoice_no, invoice.vessel_name, invoice.voyage].some((text) =>
        text?.toLowerCase().includes(needle),
      ),
  );
  const groups = new Map<string, InvoiceSummary[]>();
  for (const invoice of shown) groups.set(groupOf(invoice), [...(groups.get(groupOf(invoice)) ?? []), invoice]);
  const selectable = (invoice: InvoiceSummary) => !currency || invoice.currency === currency;

  function tick(group: InvoiceSummary[], on: boolean): void {
    const ids = new Set(group.filter(selectable).map((invoice) => invoice.id));
    const kept = Object.fromEntries(Object.entries(lines).filter(([key]) => !ids.has(key)));
    if (!on) return onChange(kept);
    const added = group
      .filter((invoice) => ids.has(invoice.id))
      .map((invoice) => [invoice.id, lines[invoice.id] ?? { amount: invoice.balance, write_off: "" }]);
    onChange({ ...kept, ...Object.fromEntries(added) });
  }

  function edit(invoiceId: string, change: Partial<InvoiceLine>): void {
    const line = lines[invoiceId];
    if (line) onChange({ ...lines, [invoiceId]: { ...line, ...change } });
  }

  return (
    <div className="flex flex-col gap-3">
      <TextField
        id={`${id}-find`}
        label="Find an invoice, vessel or voyage"
        value={find}
        onChange={setFind}
        placeholder="e.g. DEDE KORKUT, 12 or 0102"
      />
      {invoices.length === 0 && (
        <p className="text-sm text-muted-foreground">This client has no open invoices.</p>
      )}
      {[...groups].map(([label, group], index) => (
        <div key={label} className="rounded-md border">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2">
            <Checkbox
              id={`${id}-group-${index}`}
              label={`All of ${label}`}
              checked={group.every((invoice) => invoice.id in lines)}
              onChange={(on) => tick(group, on)}
              disabled={!group.some(selectable)}
            />
            <span className="text-xs text-muted-foreground">
              {group.length} open · balance{" "}
              {formatTotals(totalsByCurrency(group, (i) => i.currency, (i) => i.balance))}
            </span>
          </div>
          <ul className="divide-y">
            {group.map((invoice) => {
              const line = lines[invoice.id];
              const number = invoice.invoice_no ?? "the invoice";
              const owing = line
                ? cents(invoice.balance) - cents(line.amount) - cents(line.write_off)
                : 0;
              return (
                <li key={invoice.id} className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <Checkbox
                      id={`${id}-invoice-${invoice.id}`}
                      label={number}
                      checked={line !== undefined}
                      onChange={(on) => tick([invoice], on)}
                      disabled={!selectable(invoice)}
                    />
                    <span className="text-xs text-muted-foreground">
                      {invoice.vessel_name ?? "No vessel"}
                      {invoice.voyage ? ` · Voyage ${invoice.voyage}` : ""} · sailed{" "}
                      {formatDate(invoice.departure)} · balance{" "}
                      {formatMoney(invoice.balance, invoice.currency)}
                    </span>
                  </div>
                  {line && (
                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                      <TextField
                        id={`${id}-amount-${invoice.id}`}
                        label={`Amount for ${number}`}
                        inputMode="decimal"
                        value={line.amount}
                        onChange={(amount) => edit(invoice.id, { amount })}
                        hint={
                          owing > 0
                            ? `Leaves ${formatMoney(centsToAmount(owing), invoice.currency)} owing`
                            : undefined
                        }
                      />
                      <TextField
                        id={`${id}-write-off-${invoice.id}`}
                        label={`Write-off on ${number}`}
                        inputMode="decimal"
                        value={line.write_off}
                        onChange={(write_off) => edit(invoice.id, { write_off })}
                        hint="A shortfall you waive; it closes the invoice, so it must clear the rest"
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
